import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <BrandLogo size="lg" />
          <span className="font-display text-2xl font-bold">TradeFlow UK</span>
        </Link>

        {!token ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-5" data-testid="reset-no-token">
            <p className="font-display font-bold text-red-700">Missing reset token</p>
            <p className="text-sm text-red-700 mt-1">
              This page must be opened from the link in your reset email.
            </p>
            <Link to="/forgot-password" className="mt-4 inline-block underline text-sm">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-5 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <p className="font-display font-bold text-green-800">Password updated</p>
            <p className="text-sm text-green-700 mt-1">Redirecting you to sign in...</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold mb-2">Set a new password</h1>
            <p className="text-zinc-600 mb-8">Pick something strong — at least 6 characters.</p>
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1"
                  data-testid="reset-confirm-input"
                />
              </div>
              {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-black hover:bg-zinc-800 text-white rounded-md py-6 font-semibold"
                data-testid="reset-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
